import { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useChauffeurSocket from "@/hooks/useChauffeurSocket";
import { errorMessage } from "@/lib/errors";

const FALLBACK_QRS = {
  im_here: "I'm here",
  im_close: "I'm close — 2 min away",
  running_late: "Running a few minutes late",
  thanks: "Thanks!",
  cancelling: "I need to cancel",
  wrong_address: "Wrong address — please call me",
  i_need_help: "I need help — please call me",
};

/**
 * RideChat — slide-up chat panel for driver ↔ passenger messaging.
 *
 * Props:
 *  • ride: the active ride doc
 *  • myEmail: current user's email (decides bubble alignment)
 *  • onClose(): collapse the panel
 *
 * Behaviour:
 *  - Loads message history on mount, then live-appends via Socket.IO
 *    `chat_message` events.
 *  - Quick-reply chips render above the input (canned text shortcuts).
 *  - Marks all messages read when the panel is opened (clears badges
 *    on the parent).
 */
export default function RideChat({ ride, myEmail, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [quickReplies, setQuickReplies] = useState(FALLBACK_QRS);
  const [sending, setSending] = useState(false);
  const { on } = useChauffeurSocket({ rideId: ride?.id });
  const scrollerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
    }, 60);
  }, []);

  // Initial load
  useEffect(() => {
    if (!ride?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/chauffeur/rides/${ride.id}/chat`);
        if (cancelled) return;
        setMessages(res.data?.items || []);
        if (res.data?.quick_replies) setQuickReplies(res.data.quick_replies);
        await api.post(`/chauffeur/rides/${ride.id}/chat/read`).catch(() => {});
        scrollToBottom();
      } catch (err) {
        if (!cancelled) toast.error(errorMessage(err, "Could not load chat"));
      }
    })();
    return () => { cancelled = true; };
  }, [ride?.id, scrollToBottom]);

  // Live append
  useEffect(() => {
    if (!ride?.id) return;
    const off = on("chat_message", (msg) => {
      if (msg.ride_id !== ride.id) return;
      setMessages((cur) => {
        // Dedupe (server echoes the sender's own message too)
        if (cur.find((m) => m.id === msg.id)) return cur;
        return [...cur, msg];
      });
      scrollToBottom();
      // If incoming from other party, auto-mark read
      if (msg.sender_email !== myEmail) {
        api.post(`/chauffeur/rides/${ride.id}/chat/read`).catch(() => {});
      }
    });
    return () => off();
  }, [ride?.id, on, myEmail, scrollToBottom]);

  const send = async (payload) => {
    if (!payload.body && !payload.quick_reply) return;
    setSending(true);
    try {
      await api.post(`/chauffeur/rides/${ride.id}/chat`, payload);
      setDraft("");
      // Socket.IO echo will append the message; no optimistic update
      // needed (avoids duplicate-then-dedupe flicker).
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't send"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="ride-chat-panel"
      className="flex flex-col h-[480px] border border-gold/30 bg-luxury-black rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-2">
          <MessageCircle size={14} className="text-gold" />
          <p className="text-[11px] uppercase tracking-wider text-gold">In-app chat</p>
          {ride.quiet_ride && (
            <span data-testid="quiet-ride-badge"
              className="ml-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-400/30">
              Quiet ride
            </span>
          )}
        </div>
        {onClose && (
          <button data-testid="chat-close-btn" onClick={onClose}
            className="text-white/50 hover:text-white"><X size={16} /></button>
        )}
      </div>

      <div ref={scrollerRef} data-testid="chat-messages"
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-black/20">
        {messages.length === 0 && (
          <p className="text-center text-[11px] text-white/30 py-6">No messages yet. Quick-reply or type to start.</p>
        )}
        {messages.map((m) => {
          const isAria = m.sender_role === "aria";
          const mine = !isAria && m.sender_email === myEmail;
          const text = m.body || m.quick_reply_text || quickReplies[m.quick_reply] || "";
          return (
            <div key={m.id} data-testid={`chat-msg-${m.id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-lg px-2.5 py-1.5 text-xs leading-relaxed ${
                isAria
                  ? "bg-gradient-to-br from-gold/25 to-gold/10 text-gold border border-gold/50 shadow-sm shadow-gold/20"
                  : mine
                    ? "bg-gold text-luxury-black"
                    : "bg-white/10 text-white border border-white/10"
              }`}>
                {isAria && (
                  <p className="flex items-center gap-1 text-[9px] uppercase tracking-wider opacity-90 mb-0.5">
                    <Sparkles size={9} className="text-gold" /> ARIA
                  </p>
                )}
                {!mine && !isAria && (
                  <p className="text-[9px] uppercase tracking-wider opacity-60 mb-0.5">
                    {m.sender_role}
                  </p>
                )}
                <p>{text}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-3 py-2 border-t border-white/10 bg-black/40 space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            data-testid="ask-aria-btn"
            onClick={() => setDraft((d) => (d.toLowerCase().startsWith("@aria") ? d : `@aria ${d}`))}
            disabled={sending}
            title="Prefix the next message with @aria to summon ARIA"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-gold/50 bg-gold/10 text-gold hover:bg-gold/20 disabled:opacity-40 transition-colors"
          >
            <Sparkles size={10} /> Ask ARIA
          </button>
          {Object.entries(quickReplies).slice(0, 4).map(([key, label]) => (
            <button
              key={key}
              data-testid={`quick-reply-${key}`}
              onClick={() => send({ quick_reply: key })}
              disabled={sending}
              className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-gold/30 text-gold/90 hover:bg-gold/10 disabled:opacity-40 transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send({ body: draft }); }}
          className="flex items-center gap-2"
        >
          <input
            data-testid="chat-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={280}
            placeholder="Type a message…"
            className="flex-1 bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none"
          />
          <button data-testid="chat-send-btn" type="submit" disabled={sending || !draft.trim()}
            className="inline-flex items-center justify-center w-10 h-10 rounded bg-gold text-luxury-black hover:bg-gold/90 disabled:opacity-40">
            <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
